import {type ComponentProps} from "react";
import {useRender} from "@base-ui/react/use-render";
import {clsx} from "clsx";
import {Button} from "./Button";
import styles from "./InputGroup.module.css";

interface InputGroupRootProps extends useRender.ComponentProps<"div"> {
	/**
	 * Marks the whole group invalid
	 * Only needed for externally driven validity: a `Field.Control` inside the group carries Base UI's own `data-invalid`,
	 * which the module already picks up.
	 */
	invalid?: boolean
}


function InputGroupRoot({className, render, invalid = false, ref, ...elementProps}: InputGroupRootProps) {
	return useRender({
		render,
		ref,
		state: {invalid},
		defaultTagName: "div",
		props: {
			"data-slot": "input-group",
			role: "group",
			className: clsx(styles.root, className),
			...elementProps,
		},
	});
}

interface InputGroupAddonProps extends useRender.ComponentProps<"span"> {
	/**
	 * Drops the padding and makes the cell as wide as a control is tall: for a single icon or checkbox.
	 */
	square?: boolean
}


function InputGroupAddon({className, render, square = false, ref, ...elementProps}: InputGroupAddonProps) {
	return useRender({
		render,
		ref,
		defaultTagName: "span",
		props: {
			"data-slot": "input-group-addon",
			className: clsx(styles.addon, square && styles.square, className),
			...elementProps,
		},
	});
}

function InputGroupButton({className, variant = "secondary", ...props}: ComponentProps<typeof Button>) {
	return (
		<Button
			data-slot="input-group-button"
			variant={variant}
			className={clsx(styles.button, className)}
			{...props}
		/>
	);
}

const InputGroup = {
	Root: InputGroupRoot,
	Addon: InputGroupAddon,
	Button: InputGroupButton,
};

export {InputGroup};
